import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import apiService from "../api/axiosInstance";
import { setIsNewUser, setUser } from "../features/auth/user.reducer";
import {
  getProfileCompletionState,
  isProfileIncomplete,
} from "../features/auth/profileCompletion";
import {
  clearParentPostAuthRedirect,
  persistParentPostAuthRedirect,
  persistParentSessionMode,
} from "../features/parentFlow/parentFlow";

const DEFAULT_REDIRECT_PATH = "/tutorinfo";
const EXPIRED_MESSAGE =
  "Session link expired. Please return to the app and try again.";
const SCRIPT_PROTOCOL = ["java", "script:"].join("");

const isSafeRedirectPath = (path) => {
  if (!path) return false;

  if (!path.startsWith("/")) return false;

  if (path.startsWith("//")) return false;

  const lower = path.toLowerCase();

  if (lower.includes("http://")) return false;

  if (lower.includes("https://")) return false;

  if (lower.includes(SCRIPT_PROTOCOL)) return false;

  return true;
};

const MobileSSOLogin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const hasStartedExchangeRef = useRef(false);

  const code = useMemo(
    () => String(searchParams.get("code") || "").trim(),
    [searchParams]
  );
  const redirectParam = useMemo(
    () => String(searchParams.get("redirect") || "").trim(),
    [searchParams]
  );
  const validRedirectPath = isSafeRedirectPath(redirectParam)
    ? redirectParam
    : DEFAULT_REDIRECT_PATH;

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      window.location.pathname === "/mobile-sso" &&
      window.location.search
    ) {
      window.history.replaceState(
        window.history.state,
        "",
        window.location.pathname
      );
    }
  }, []);

  useEffect(() => {
    if (!code) {
      setStatus("error");
      return;
    }

    if (hasStartedExchangeRef.current) {
      return;
    }

    hasStartedExchangeRef.current = true;

    let isActive = true;

    const exchangeCode = async () => {
      try {
        const response = await apiService.post("api/mobile-web-sso/exchange", {
          code,
        });
        const responseBody = response?.data || {};
        const profileData = responseBody?.data || {};

        if (
          responseBody?.status !== "Success" ||
          !profileData ||
          typeof profileData !== "object" ||
          !String(profileData.jwtoken || "").trim()
        ) {
          throw new Error("Invalid mobile SSO response.");
        }

        if (!isActive) return;

        persistParentSessionMode();
        dispatch(
          setUser({
            user: profileData,
            token: profileData.jwtoken,
          })
        );

        if (isProfileIncomplete(profileData)) {
          persistParentPostAuthRedirect(validRedirectPath);
          const { isNameMissing, isDobMissing, isAvatarMissing } =
            getProfileCompletionState(profileData);

          dispatch(setIsNewUser(true));
          navigate("/afterlogin", {
            replace: true,
            state: {
              loginData: profileData,
              showNameTab: isNameMissing,
              showDobTab: isDobMissing,
              showAvatarTab: isAvatarMissing,
              redirectAfterComplete: validRedirectPath,
            },
          });
          return;
        }

        clearParentPostAuthRedirect();
        navigate(validRedirectPath, { replace: true });
      } catch (_error) {
        if (!isActive) return;
        setStatus("error");
      }
    };

    void exchangeCode();

    return () => {
      isActive = false;
    };
  }, [code, dispatch, navigate, validRedirectPath]);

  if (status === "error") {
    return (
      <div className="mobile-sso-page flex min-h-screen items-center justify-center bg-[#f8f3ea] px-4">
        <div className="w-full max-w-md rounded-[28px] border border-[#e5dacb] bg-white px-8 py-10 text-center shadow-[0_24px_60px_rgba(141,107,70,0.16)]">
          <h1 className="text-2xl font-bold text-[#0B2B66]">Link expired</h1>
          <p className="mt-3 text-base leading-7 text-[#5f6576]">
            {EXPIRED_MESSAGE}
          </p>
          <button
            type="button"
            onClick={() => navigate("/", { replace: true })}
            className="mt-6 inline-flex items-center justify-center rounded-full bg-[#08059B] px-6 py-3 text-base font-bold text-white transition hover:bg-[#01044F]"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-sso-page flex min-h-screen items-center justify-center bg-[#f8f3ea] px-4">
      <div className="w-full max-w-md rounded-[28px] border border-[#e5dacb] bg-white px-8 py-10 text-center shadow-[0_24px_60px_rgba(141,107,70,0.16)]">
        <Loader2
          className="mx-auto h-10 w-10 animate-spin text-[#08059B]"
          aria-hidden="true"
        />
        <p className="mt-4 text-xl font-bold text-[#0B2B66]">
          Signing you in...
        </p>
      </div>
    </div>
  );
};

export default MobileSSOLogin;
